import { JSX } from "react";
import "../../../../App.css";
import LegacyApiClient from "../../../../Providers.Api/LegacyApiClient";
import { appContext } from "../../../../AppContext";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { INewIdentityProvider, IdentityProvider } from "./DataModels";
import { IIdentityProvidePostModel, IIdentityPutModel, SyncTypeEnum } from "../../../../Providers.FadxPlatformApiClientV2/FadxPlatformApiClientV2";
import { IRouterProps, withRouting } from "@iconics/components";
import { SerendieSymbol } from "@serendie/symbols";
import { DateTime } from "luxon";
import { MultilingualTextField } from "@iconics/components";
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";
import { Switch } from "@serendie/ui";
import EditScimSettings from "./EditScimSettings";
import { Validator } from '@iconics/core';
import { Guid, TypeHelper } from "@iconics/core";
import { Button, ButtonBar, Card, DeleteButton, GridContainer, GridItem, IPageNavSection, LayoutContainer, LayoutContent, LayoutFooter, LayoutRight, LeftButtons, PageNav, RightButtons, Select, TextField } from "@iconics/components";
import { SecurityProviderId_Native, SyncronisationType } from "../../../../Providers.StaticData/StaticDataProvider";

class EditIdentityProvider extends IbssPage<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);
    private get labels() { return appContext().labels; }
    private get alert() { return appContext().alert; }
    private securityProviderOptions: { label: string, value: number }[] = [];
    private passwordComplexityOptions: { label: string, value: number }[] = [];
    private synchronisationTypes: { label: string, value: SyncronisationType }[] = [];
    private languageValuesHelper: LanguageValuesHelper;

    private nativeCardKey = Guid.new().toString();
    private aadB2cCardKey = Guid.new().toString();
    private oktaAuth0CardKey = Guid.new().toString();

    private get identityProviderId(): number
    {
        return TypeHelper.toNumber(this.props.params.id ?? null) ?? 0;
    }

    private get isNew(): boolean
    {
        return this.identityProviderId === 0;
    }

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);
        this.securityProviderOptions = appContext().staticDataProvider.getSecurityProviders().map(i => ({ value: i.id, label: i.name }));

        this.state = {
            canUpdate: true,
            canDelete: false,
            activePage: 0,
            sections: [],

            // common properties
            name: "",
            nameError: "",
            displayName: this.languageValuesHelper.default(),
            securityProviderTypeId: 0,
            securityProviderTypeName: this.securityProviderOptions[0]?.label ?? '',
            isDefault: false,
            concurrencyTimestamp: '',

            // native properties
            minPasswordLength: 0,
            minPasswordLengthError: '',
            maxPasswordAge: 0,
            passwordComplexity: 2,
            passwordComplexityLabel: this.labels.IdentityProvider_PasswordComplexity_TwoDifferentTypes,

            // 3rd party properties
            clientId: "",
            clientSecret: "",
            clientSecretUpdated: false,
            tenantId: "",
            tenantDomain: "",
            nameClaimType: "",
            additionalAudiences: "",
            filteredSynchronisationTypes: appContext().staticDataProvider.getSyncTypesBySecurityProvider(SecurityProviderId_Native).map(i => ({ value: i.id, label: i.name })),
            synchronisationTypeLabel: this.labels.IdentityProviders_SyncType_None,
            synchronisationType: 'None',
            lastSync: null,
            lastSyncStatus: "",
            lastSyncInfo: "",
            identityProviderId: '',
            scimSecurityToken: '',

            scimAccessToken: '',
            hasPendingChanges: false,
            scimDialogOpen: false,
        };
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.Platform_IdentityProvider_PageTitle;
        this.backUrl = '/security-identity-providers';

        this.synchronisationTypes = appContext().staticDataProvider.getSyncTypes().map(i => ({ value: i.id, label: i.name }));

        this.passwordComplexityOptions = [
            { label: this.labels.IdentityProvider_PasswordComplexity_AnyType, value: 1 },
            { label: this.labels.IdentityProvider_PasswordComplexity_TwoDifferentTypes, value: 2 },
            { label: this.labels.IdentityProvider_PasswordComplexity_ThreeDifferentTypes, value: 3 },
            { label: this.labels.IdentityProvider_PasswordComplexity_FourDifferentTypes, value: 4 },
        ];

        if (!this.isNew)
        {
            await this.loadIdentityProvider();
        }
        this.validator
            .addInput('name', () => this.state.name, i => this.setState({ nameError: i }))
            .isRequired({ errorLabel: this.labels.Platform_IdentityProvider_Edit_Required });
        if (this.state.securityProviderTypeId === 0)
        {
            this.validator
                .addInput('minPasswordLength', () => this.state.minPasswordLength.toString(), i => this.setState({ minPasswordLengthError: i }))
                .isGreaterThan(0, { errorLabel: this.labels.Platform_IdentityProvider_ZeroNotAcceptable });
        }

    }

    public async componentDidUpdate(prevProps: Readonly<IProps>): Promise<void> 
    {
        if (prevProps.params.id !== this.props.params.id)
        {
            await this.loadIdentityProvider();
        }
    }

    private async loadIdentityProvider(): Promise<void>
    {
        await this.loading(async () =>
        {
            const identityProvider = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.identityProviders.byId.get<IdentityProvider>({
                select: IdentityProvider,
                tenancy: appContext().tenantId,
                id: this.identityProviderId,
            });

            await this.setStateAsync({
                canUpdate: appContext().localStorageProvider.hasRight('ADMINPORTAL.IdentityProviders.Update'),
                canDelete: appContext().localStorageProvider.hasRight('ADMINPORTAL.IdentityProviders.Delete'),

                // common properties
                name: identityProvider.Name,
                displayName: this.languageValuesHelper.fromString(identityProvider.DisplayName),
                securityProviderTypeId: identityProvider.SecurityProviderTypeId,
                securityProviderTypeName: this.securityProviderOptions.find(x => x.value === identityProvider.SecurityProviderTypeId)?.label ?? '',
                isDefault: identityProvider.IsDefault,
                concurrencyTimestamp: identityProvider['@odata.etag'],

                // native properties
                minPasswordLength: identityProvider.MinPasswordLength,
                maxPasswordAge: identityProvider.MaxPasswordAge,
                passwordComplexity: identityProvider.PasswordComplexity,
                passwordComplexityLabel: this.passwordComplexityOptions.find(x => x.value === identityProvider.PasswordComplexity)?.label ?? '',

                // 3rd party properties
                clientId: identityProvider.ClientId,
                clientSecret: this.maskString(identityProvider.ClientSecret ?? ''),
                clientSecretUpdated: false,
                tenantId: identityProvider.TenantId,
                tenantDomain: identityProvider.TenantDomain,
                nameClaimType: identityProvider.NameClaimType,
                additionalAudiences: identityProvider.AdditionalAudiences,
                synchronisationType: identityProvider.SyncType,
                filteredSynchronisationTypes: appContext().staticDataProvider.getSyncTypesBySecurityProvider(identityProvider.SecurityProviderTypeId).map(i => ({ value: i.id, label: i.name })),
                synchronisationTypeLabel: this.synchronisationTypes.find(x => x.value === identityProvider.SyncType)?.label ?? '',
                lastSync: identityProvider.LastSync,
                lastSyncStatus: (identityProvider.LastSync == null ? '' : this.getLastSyncStatusName(identityProvider.LastSyncStatus)),
                lastSyncInfo: JSON.stringify(identityProvider.LastSyncInfo, undefined, 4),
                identityProviderId: identityProvider.IdentityProviderId,
                scimSecurityToken: identityProvider.ScimSecurityToken,
                scimAccessToken: '',

                hasPendingChanges: false,
            });
        });
    }

    private maskString(value: string): string
    {
        if (value.length <= 3)
        {
            return value;
        }
        const maskedString = value.slice(0, 3) + '*'.repeat(value.length - 3);
        return maskedString;
    }

    private getLastSyncStatusName(id: number)
    {
        switch (id)
        {
            case 0:
                return this.labels.Platform_IdentityProvider_SyncNow_Success;
            case 1:
                return this.labels.Platform_IdentityProvider_SyncNow_Error;
            default:
                return this.labels.funcUnknown_S;
        }
    }

    private async handleSyncAad(): Promise<void>
    {
        if (this.isNew || this.state.synchronisationType != 'GraphAPI')
        {
            return;
        }
        await this.loading(async () =>
        {
            await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.identityProviders.byId.syncExternalProvider.post({
                tenancy: appContext().tenantId,
                id: this.identityProviderId.toString(),
            });
            setTimeout(() => this.loadIdentityProvider(), 1000);
        });
    }

    private handleShowSyncMessage(): void
    {
        this.alert.show("", this.state.lastSyncInfo);
    }

    private async handleDelete(): Promise<void>
    {
        await this.loading(async () =>
        {
            await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.identityProviders.byId.delete({
                id: this.identityProviderId,
                tenancy: appContext().tenantId,
            });
            this.props.navigate("/security-identity-providers");
        });
    }

    private async handleSave(): Promise<void>
    {
        if (!this.validator.validateAll())
        {
            return;
        }
        await this.loading(async () =>
        {
            const isNative = (this.state.securityProviderTypeId === 0);
            if (this.isNew)
            {
                const payload: IIdentityProvidePostModel =
                {
                    SecurityProviderTypeId: this.state.securityProviderTypeId,
                    ClientId: this.state.clientId,
                    TenantDomain: this.state.tenantDomain,
                    TenantId: this.state.tenantId,
                    Name: this.state.name,
                    DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                    AdditionalAudiences: (isNative ? "" : this.state.additionalAudiences),
                    NameClaimType: this.state.nameClaimType,
                    MaxPasswordAge: (isNative ? this.state.maxPasswordAge : 0),
                    MinPasswordLength: (isNative ? this.state.minPasswordLength : 0),
                    PasswordComplexity: (isNative ? this.state.passwordComplexity : 0),
                    IsDefault: this.state.isDefault,
                    ClientSecret: (this.state.clientSecretUpdated ? this.state.clientSecret : undefined),
                    SyncType: this.state.synchronisationType,
                };

                const newIdentityProvider = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.identityProviders.post<INewIdentityProvider>({
                    body: payload,
                    tenancy: appContext().tenantId,
                });
                this.props.navigate("/security-identity-providers/details/" + newIdentityProvider.Id, { replace: true });
            }
            else
            {
                const payload: IIdentityPutModel =
                {
                    SecurityProviderTypeId: this.state.securityProviderTypeId,
                    ClientId: this.state.clientId,
                    TenantDomain: this.state.tenantDomain,
                    TenantId: this.state.tenantId,
                    Name: this.state.name,
                    DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                    AdditionalAudiences: (isNative ? "" : this.state.additionalAudiences),
                    NameClaimType: this.state.nameClaimType,
                    MaxPasswordAge: (isNative ? this.state.maxPasswordAge : 0),
                    MinPasswordLength: (isNative ? this.state.minPasswordLength : 0),
                    PasswordComplexity: (isNative ? this.state.passwordComplexity : 0),
                    IsDefault: this.state.isDefault,
                    ClientSecret: (this.state.clientSecretUpdated ? this.state.clientSecret : undefined),
                    SyncType: this.state.synchronisationType,
                    ConcurrencyStamp: this.state.concurrencyTimestamp
                };

                await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.identityProviders.byId.patch({
                    body: payload,
                    id: this.identityProviderId,
                    tenancy: appContext().tenantId,
                });
                this.props.navigate("/security-identity-providers");
            }
        });
    }

    private async handleSecurityTypeChanged(value: number): Promise<void> 
    {
        if (value === 0)
        {
            this.validator
                .addInput('minPasswordLength', () => this.state.minPasswordLength.toString(), i => this.setState({ minPasswordLengthError: i }))
                .isGreaterThan(0, { errorLabel: this.labels.Platform_IdentityProvider_ZeroNotAcceptable });
        }
        else
        {
            this.validator
                .removeInput('minPasswordLength');
        }

        this.setState({ hasPendingChanges: true, securityProviderTypeId: value }, () => this.validator.validate('securityProviderTypeId'));
        await this.setStateAsync({
            securityProviderTypeName: this.securityProviderOptions.find(x => x.value === value)?.label ?? '',
            filteredSynchronisationTypes: appContext().staticDataProvider.getSyncTypesBySecurityProvider(value).map(i => ({ value: i.id, label: i.name })),
            synchronisationTypeLabel: 'None',
            synchronisationType: 'None',
        });
    }

    private handleClientSecretChange(value: string): void 
    {
        this.setState({ hasPendingChanges: true, clientSecret: value }, () => this.validator.validate('clientSecret'));
        this.setState({ clientSecretUpdated: true });
    }

    private renderCommonProperties(): JSX.Element
    {
        return (
            <Card
                icon={<SerendieSymbol name='login' />}
                title={this.labels.Platform_IdentityProvider_CommonDetailsTitle}
                subTitle={this.labels.Platform_IdentityProvider_CommonDetailsSubTitle}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 0) }))}
            >
                <GridContainer>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.IdentityProvider_Name}
                            placeholder={this.labels.IdentityProvider_Name}
                            value={this.state.name}
                            onChange={e => this.setState({ hasPendingChanges: true, name: e.target.value }, () => this.validator.validate('name'))}
                            required
                            invalid={!!this.state.nameError}
                            invalidMessage={this.state.nameError}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <MultilingualTextField
                            label={this.labels.IdentityProvider_DisplayName}
                            placeholder={this.labels.IdentityProvider_DisplayName}
                            value={this.state.displayName}
                            onChange={value => this.setState({ hasPendingChanges: true, displayName: value })}
                            localisation={appContext().localisation}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <Select
                            label={this.labels.IdentityProvider_SecurityProviderTypeId}
                            placeholder={this.state.securityProviderTypeName}
                            onSelect={(e) => this.handleSecurityTypeChanged(Number.parseInt(e.value))}
                            items={this.securityProviderOptions.map(x => ({ label: x.label, value: x.value.toString() }))}
                            required
                            disabled={!this.isNew}
                        />
                    </GridItem>
                    <GridItem>
                        <Switch
                            label={this.labels.IdentityProvider_IsDefault}
                            checked={this.state.isDefault}
                            onCheckedChange={(e) => this.setState({ hasPendingChanges: true, isDefault: e.checked as boolean })}
                        />
                    </GridItem>
                </GridContainer>
            </Card>
        );
    }

    private renderNativeProperties(): JSX.Element
    {
        return (
            <Card
                key={this.nativeCardKey}
                title={this.labels.Platform_IdentityProvider_NativeDetailsTitle}
                subTitle={this.labels.Platform_IdentityProvider_NativeDetailsSubTitle}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 1) }))}
            >
                <GridContainer>
                    <GridItem expanded={6}>
                        <TextField
                            name="minPasswordLength"
                            label={this.labels.IdentityProvider_MinPasswordLength}
                            value={this.state.minPasswordLength}
                            type='number'
                            min={1}
                            placeholder={this.labels.IdentityProvider_MinPasswordLength}
                            onChange={e => this.setState({ hasPendingChanges: true, minPasswordLength: Number.parseInt(e.target.value) }, () => this.validator.validate('minPasswordLength'))}
                            invalid={!!this.state.minPasswordLengthError}
                            invalidMessage={this.state.minPasswordLengthError}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <TextField
                            name="maxPasswordAge"
                            label={this.labels.IdentityProvider_MaxPasswordAge}
                            value={this.state.maxPasswordAge}
                            type='number'
                            placeholder={this.labels.IdentityProvider_MaxPasswordAge}
                            onChange={e => this.setState({ hasPendingChanges: true, maxPasswordAge: Number.parseInt(e.target.value) })}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <Select
                            label={this.labels.IdentityProvider_PasswordComplexity}
                            placeholder={this.state.passwordComplexityLabel}
                            onSelect={(e) => this.setState({ hasPendingChanges: true, passwordComplexity: Number.parseInt(e.value) })}
                            items={this.passwordComplexityOptions.map(x => ({ label: x.label, value: x.value.toString() }))}
                        />
                    </GridItem>
                </GridContainer>
            </Card>
        );
    }

    private renderAadB2CProperties(title: string, subTitle: string): JSX.Element
    {
        return (
            <Card
                key={this.aadB2cCardKey}
                title={title}
                subTitle={this.labels.Platform_IdentityProvider_B2CDetailsSubTitle}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 2) }))}
            >
                <GridContainer>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.IdentityProvider_ClientId}
                            value={this.state.clientId}
                            placeholder={this.labels.IdentityProvider_ClientId}
                            onChange={e => this.setState({ hasPendingChanges: true, clientId: e.target.value })}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.IdentityProvider_ClientSecret}
                            value={this.state.clientSecret}
                            placeholder={this.labels.IdentityProvider_ClientSecret}
                            onChange={e => this.handleClientSecretChange(e.target.value)}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.IdentityProvider_TenantId}
                            value={this.state.tenantId}
                            placeholder={this.labels.IdentityProvider_TenantId}
                            onChange={e => this.setState({ hasPendingChanges: true, tenantId: e.target.value })}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.IdentityProvider_TenantDomain}
                            value={this.state.tenantDomain}
                            placeholder={this.labels.IdentityProvider_TenantDomain}
                            onChange={e => this.setState({ hasPendingChanges: true, tenantDomain: e.target.value })}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.IdentityProvider_NameClaimType}
                            value={this.state.nameClaimType}
                            placeholder={this.labels.IdentityProvider_NameClaimType}
                            onChange={e => this.setState({ hasPendingChanges: true, nameClaimType: e.target.value })}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.IdentityProvider_AdditionalAudiences}
                            value={this.state.additionalAudiences}
                            placeholder={this.labels.IdentityProvider_AdditionalAudiences}
                            onChange={e => this.setState({ hasPendingChanges: true, additionalAudiences: e.target.value })}
                        />
                    </GridItem>
                    {this.renderSyncType()}
                </GridContainer>
            </Card>
        );
    }

    private renderIdentityProviderProperties(): JSX.Element
    {
        switch (this.state.securityProviderTypeId)
        {
            case 0: return this.renderNativeProperties();
            case 1: return this.renderAadB2CProperties(this.labels.Platform_IdentityProvider_AADDetailsTitle, this.labels.Platform_IdentityProvider_AADDetailsSubTitle);
            case 2: return this.renderOktaAuth0Properties(this.labels.Platform_IdentityProvider_OktaDetailsTitle, this.labels.Platform_IdentityProvider_OktaDetailsSubTitle);
            case 3: return this.renderOktaAuth0Properties(this.labels.Platform_IdentityProvider_Auth0DetailsTitle, this.labels.Platform_IdentityProvider_Auth0DetailsSubTitle);
            case 4: return this.renderAadB2CProperties(this.labels.Platform_IdentityProvider_B2CDetailsTitle, this.labels.Platform_IdentityProvider_B2CDetailsSubTitle);
            default: return (<></>);
        }
    }

    private renderOktaAuth0Properties(title: string, subTitle: string): JSX.Element
    {
        return (
            <Card
                key={this.oktaAuth0CardKey}
                title={title}
                subTitle={subTitle}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 3) }))}
            >
                <GridContainer>
                    <GridItem expanded={6}>
                        <TextField
                            name="clientId"
                            label={this.labels.IdentityProvider_ClientId}
                            value={this.state.clientId}
                            placeholder={this.labels.IdentityProvider_ClientId}
                            onChange={e => this.setState({ hasPendingChanges: true, clientId: e.target.value })}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.IdentityProvider_TenantDomain}
                            value={this.state.tenantDomain}
                            placeholder={this.labels.IdentityProvider_TenantDomain}
                            onChange={e => this.setState({ hasPendingChanges: true, tenantDomain: e.target.value })}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.IdentityProvider_NameClaimType}
                            value={this.state.nameClaimType}
                            placeholder={this.labels.IdentityProvider_NameClaimType}
                            onChange={e => this.setState({ hasPendingChanges: true, nameClaimType: e.target.value })}
                        />
                    </GridItem>
                    {this.renderSyncType()}
                </GridContainer>
            </Card>
        );
    }

    private renderSyncType(): JSX.Element
    {
        return (
            <>
                <GridItem expanded={6}>
                    <Select
                        items={this.state.filteredSynchronisationTypes}
                        label={this.labels.IdentityProvider_SyncType}
                        placeholder={this.state.synchronisationTypeLabel}
                        onSelect={e => this.setState({ hasPendingChanges: true, synchronisationType: e.value as SyncronisationType })}
                    />
                </GridItem>
                {
                    !this.isNew && this.state.synchronisationType === 'GraphAPI' &&
                    <GridItem>
                        {this.renderSyncTypeGraphApi()}
                    </GridItem>
                }
                {
                    this.state.synchronisationType === 'SCIMv2' &&
                    <GridItem>
                        {this.renderSyncTypeScimV2()}
                    </GridItem>
                }
            </>
        )
    }

    private renderSyncTypeGraphApi(): JSX.Element
    {
        return (
            <>
                <Card
                    title={this.labels.Platform_IdentityProviders_SyncType_GraphAPI_Title}
                    subTitle={this.labels.Platform_IdentityProviders_SyncType_GraphAPI_SubTitle}
                    onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 4) }))}
                >
                    <GridContainer>
                        <GridItem expanded={6}>
                            <TextField
                                label={this.labels.IdentityProvider_LastSync}
                                value={this.state.lastSync?.toLocaleDateTimeString() ?? ''}
                                disabled
                                placeholder={this.labels.IdentityProvider_LastSync}
                            />
                        </GridItem>
                        <GridItem expanded={6}>
                            <TextField
                                label={this.labels.IdentityProvider_LastSyncStatus}
                                value={this.state.lastSyncStatus}
                                disabled
                                placeholder={this.labels.IdentityProvider_LastSyncStatus}
                            />
                        </GridItem>
                        <GridItem compact={3} expanded={2}>
                            <Button
                                color="primary"
                                onClick={() => this.handleSyncAad()}
                            >
                                {this.labels.Platform_IdentityProvider_SyncNow}
                            </Button>
                        </GridItem>

                        {
                            this.state.lastSync &&
                            <GridItem compact={3} expanded={2}>
                                <Button
                                    styleType="outlined"
                                    color="primary"
                                    onClick={() => this.handleShowSyncMessage()}
                                >
                                    {this.labels.Platform_IdentityProvider_LastSyncShowError}
                                </Button>
                            </GridItem>
                        }
                    </GridContainer>
                </Card>
            </>
        );
    }

    private renderSyncTypeScimV2(): JSX.Element
    {
        return (
            <EditScimSettings
                id={this.identityProviderId}
                hasPendingChanges={this.state.hasPendingChanges}
                identityProviderId={this.state.identityProviderId}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 5) }))}
                scimSecurityToken={this.state.scimSecurityToken}
                reload={() => this.loadIdentityProvider()}

            />
        );
    }

    public render(): JSX.Element
    {
        return (
            <LayoutContainer>
                <LayoutContent>
                    <GridContainer
                        spacing={5}
                    >
                        <GridItem>
                            {this.renderCommonProperties()}
                        </GridItem>
                        <GridItem>
                            {this.renderIdentityProviderProperties()}
                        </GridItem>
                    </GridContainer>
                </LayoutContent>
                <LayoutFooter>
                    <ButtonBar>
                        <LeftButtons>
                            {
                                !this.isNew &&
                                <DeleteButton
                                    onDelete={() => this.handleDelete()}
                                    dialogMessage={this.labels.Platform_IdentityProvider_ConfirmDeleteMsg}
                                />
                            }
                        </LeftButtons>
                        <RightButtons>
                            <Button
                                styleType='outlined'
                                onClick={() => this.props.navigate(`/security-identity-providers`)}
                            >
                                {this.labels.Platform_CommonAction_Cancel}
                            </Button>
                            <Button
                                styleType='filled'
                                disabled={!this.validator.isValid}
                                onClick={() => this.handleSave()}
                            >
                                {this.labels.Platform_CommonAction_Save}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </LayoutFooter>
                <LayoutRight>
                    <PageNav
                        sections={this.state.sections}
                    />
                </LayoutRight>
            </LayoutContainer>
        );
    }

}

export default withRouting(EditIdentityProvider);

export interface IProps extends IRouterProps<IMatchParams>
{
}

export interface IMatchParams
{
    id: string | undefined;
}

export interface IState
{
    canUpdate: boolean;
    canDelete: boolean;
    activePage: number;
    sections: IPageNavSection[];

    // common properties
    name: string;
    nameError: string;
    displayName: ILanguageValues;
    securityProviderTypeId: number;
    securityProviderTypeName: string;
    isDefault: boolean;
    concurrencyTimestamp: string;

    // native properties
    minPasswordLength: number;
    minPasswordLengthError: string;
    maxPasswordAge: number;
    passwordComplexity: number;
    passwordComplexityLabel: string;

    // 3rd party properties
    clientId: string;
    clientSecret: string;
    clientSecretUpdated: boolean;
    tenantId: string;
    tenantDomain: string;
    nameClaimType: string;
    additionalAudiences: string;
    filteredSynchronisationTypes: { value: SyncronisationType, label: string }[]
    synchronisationTypeLabel: string;
    synchronisationType: SyncTypeEnum;
    lastSync: DateTime | null;
    lastSyncStatus: string;
    lastSyncInfo: string;
    identityProviderId: string;
    scimSecurityToken: string;
    scimAccessToken: string;
    hasPendingChanges: boolean;
    scimDialogOpen: boolean;
}
